// A small unobtrusive overlay that prompts mobile users to rotate their device
// to landscape orientation, since the in-game Konva UI is laid out for a 16:9
// landscape stage. The overlay is only visible while the window is in portrait
// orientation and only while the user is on a game screen. The user can dismiss
// the prompt; the dismissal lasts for the rest of the session (and reappears on
// the next page load, which is desirable in case they rotate later).

const OVERLAY_ID = "rotate-device-overlay";
const STYLE_ID = "rotate-device-overlay-style";
const DISMISS_KEY = "hanabPortraitOverlayDismissedAt";
// Dismissal lasts this long before the prompt reappears (in ms). Keep it short
// so it nags only mildly.
const DISMISS_TTL_MS = 30 * 60 * 1000; // 30 minutes

let active = false;
let resizeListener: (() => void) | null = null;

function isDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (raw === null) return false;
    const ts = Number.parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Ignore quota / privacy-mode errors.
  }
}

function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
#${OVERLAY_ID} {
  position: fixed;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  z-index: 10000;
  display: none;
  max-width: calc(100vw - 24px);
  padding: 10px 14px;
  background: rgba(20, 20, 20, 0.92);
  color: #f4f4f4;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
  font-family: "Source Sans Pro", sans-serif;
  font-size: 14px;
  line-height: 1.35;
  text-align: center;
  pointer-events: auto;
}
#${OVERLAY_ID}.visible { display: flex; align-items: center; gap: 10px; }
#${OVERLAY_ID} .rotate-icon {
  display: inline-block;
  font-size: 18px;
  line-height: 1;
}
#${OVERLAY_ID} .rotate-text {
  flex: 1 1 auto;
}
#${OVERLAY_ID} .rotate-dismiss {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 0;
  border-radius: 6px;
  padding: 4px 10px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}
#${OVERLAY_ID} .rotate-dismiss:hover { background: rgba(255, 255, 255, 0.25); }
@media (orientation: landscape) {
  #${OVERLAY_ID} { display: none !important; }
}
`;
  document.head.append(style);
}

function ensureOverlayElement(): HTMLElement {
  let el = document.getElementById(OVERLAY_ID);
  if (el !== null) return el;
  el = document.createElement("div");
  el.id = OVERLAY_ID;
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.innerHTML = `
    <span class="rotate-icon" aria-hidden="true">📱</span>
    <span class="rotate-text">Rotate your device to <strong>landscape</strong> for the best experience.</span>
    <button type="button" class="rotate-dismiss" aria-label="Dismiss">Dismiss</button>
  `;
  document.body.append(el);
  const dismissBtn = el.querySelector<HTMLButtonElement>(".rotate-dismiss");
  if (dismissBtn !== null) {
    dismissBtn.addEventListener("click", () => {
      markDismissed();
      hide();
    });
  }
  return el;
}

function show(): void {
  injectStyles();
  const el = ensureOverlayElement();
  el.classList.add("visible");
}

function hide(): void {
  const el = document.getElementById(OVERLAY_ID);
  if (el !== null) el.classList.remove("visible");
}

function update(): void {
  if (!active) {
    hide();
    return;
  }
  if (isPortrait() && !isDismissed()) {
    show();
  } else {
    hide();
  }
}

export function enablePortraitOverlay(): void {
  active = true;
  if (resizeListener === null) {
    resizeListener = () => update();
    window.addEventListener("resize", resizeListener);
    window.addEventListener("orientationchange", resizeListener);
  }
  update();
}

export function disablePortraitOverlay(): void {
  active = false;
  if (resizeListener !== null) {
    window.removeEventListener("resize", resizeListener);
    window.removeEventListener("orientationchange", resizeListener);
    resizeListener = null;
  }
  hide();
}
